import React, { useState, useEffect } from "react";
import { ChatHeaderControls } from "../chat-header/ChatHeaderControls";
import { ChatMessages } from "../chat-messages/ChatMessages";
import { ChatInputBar } from "../chat-input/ChatInputBar";
import { ChatWelcome } from "../ChatWelcome";
import { useChatSession } from "@/hooks/useChatSession";
import { createChatFromStudyInput } from "@/lib/chat/createChatFromStudyInput";
import { updateChatMessage } from "@/lib/chat/updateChatMessage";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { ChatTabId } from "../ChatRoot";
import { UsageLimitExceededError } from "@/hooks/useUsageLimits";
import { useUsageLimitContext } from "@/context/UsageLimitContext";
import { supabase } from "@/integrations/supabase/client";
import type { Plan } from "@/lib/ai/models";

interface ChatPanelProps {
  isMobile?: boolean;
  initialMessages?: any[];
  chatId?: string;
  onSwitchTab?: (tab: ChatTabId) => void;
  pendingMessage?: string | null;
  onPendingMessageConsumed?: () => void;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ 
  isMobile, 
  initialMessages = [], 
  chatId, 
  onSwitchTab,
  pendingMessage,
  onPendingMessageConsumed,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  
  // Get limit error context - wrapped in try-catch for when context isn't available
  let showLimitError: ((error: UsageLimitExceededError) => void) | null = null;
  let setShowPricingDialog: ((show: boolean) => void) | null = null;
  try {
    const context = useUsageLimitContext();
    showLimitError = context.showLimitError;
    setShowPricingDialog = context.setShowPricingDialog;
  } catch {
    // Context not available, limit errors won't be shown via banner
  }
  
  // Use the chat session hook for AI-powered chat
  const { messages, isStreaming, sendMessage, initialSearchEnabled, primarySourceContentId } = useChatSession(chatId);
  
  const [localMessages, setLocalMessages] = useState(initialMessages);
  const [replyToText, setReplyToText] = useState<string | null>(null);
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);
  const [selectedModel, setSelectedModel] = useState("auto");
  const [userPlan, setUserPlan] = useState<Plan>("free");

  // Fetch subscription status
  useEffect(() => {
    if (!user) return;
    const fetchPlan = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_status")
        .eq("id", user.id)
        .single();
      if (data?.subscription_status === "active") {
        setUserPlan("pro");
      }
    };
    fetchPlan();
  }, [user]);

  // Initialize search state from chat metadata
  useEffect(() => {
    if (initialSearchEnabled) {
      setIsSearchEnabled(true);
    }
  }, [initialSearchEnabled]);

  // Use messages from hook if chatId exists, otherwise use initialMessages
  const displayMessages = chatId ? messages : localMessages;

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [displayMessages, isStreaming]);

  // Handle pending message from quiz helper
  useEffect(() => {
    if (pendingMessage && chatId && !isStreaming) {
      handleSendMessage(pendingMessage, []);
      onPendingMessageConsumed?.();
    }
  }, [pendingMessage, chatId, isStreaming]);

  const handleSendMessage = async (
    text: string,
    attachments: File[],
    options?: { studyModeTechniqueId?: string | null },
  ) => {
    try {
      // If no chatId, create a new chat first
      if (!chatId) {
        if (!user) {
          toast.error("Please sign in to send messages");
          return;
        }
        
        const newChat = await createChatFromStudyInput({
          userId: user.id,
          firstMessage: text,
          studyModeTechniqueId: options?.studyModeTechniqueId ?? null,
        });

        // Navigate to the new chat
        navigate(`/chat/${newChat.id}`);
        return;
      }

      // If chatId exists, use the hook's sendMessage which handles AI streaming and auth
      try {
        await sendMessage(text, {
          searchEnabled: isSearchEnabled,
          model: selectedModel,
          studyModeTechniqueId: options?.studyModeTechniqueId ?? null,
        });
      } catch (error) {
        if (error instanceof UsageLimitExceededError) {
          if (showLimitError) {
            showLimitError(error);
          } else {
            toast.error("You've reached your daily chat limit. Upgrade to continue.");
          }
          return;
        }
        throw error;
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message");
    }
  };

  const handleEditMessage = async (messageId: string, newText: string) => {
    if (!user || !chatId) {
      toast.error("Unable to edit message");
      return;
    }

    try {
      await updateChatMessage({
        messageId,
        newMessage: newText,
      });

      toast.success("Message updated - refresh to see AI's new response");
    } catch (error) {
      console.error("Error editing message:", error);
      toast.error("Failed to edit message");
    }
  };

  const handleExampleClick = (question: string) => {
    handleSendMessage(question, []);
  };

  const handleSetReplyText = (text: string) => {
    setReplyToText(text);
  };

  const handleUpgradeRequest = () => {
    setShowPricingDialog?.(true);
  };
  
  return (
    <div
      className={[
        "relative flex w-full flex-col px-0 h-full",
        isMobile ? "p-4" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Top-right controls */}
      <div className="absolute right-0 top-0 z-40 -mt-1 pr-2 sm:right-2 md:right-4 lg:right-8">
        <ChatHeaderControls />
      </div>

      {/* Messages or Welcome Screen */}
      <ScrollArea className="flex-1 min-h-0">
        <div data-chat-container="true" className="h-full">
          {!chatId || displayMessages.length === 0 ? (
            <ChatWelcome onExampleClick={handleExampleClick} />
          ) : (
            <div className="mx-auto mt-10 flex w-full max-w-3xl flex-grow flex-col justify-start px-4 sm:px-4">
              <ChatMessages 
                messages={displayMessages} 
                isAITyping={isStreaming}
                chatId={chatId}
                sourceContentId={primarySourceContentId}
                onEditMessage={handleEditMessage}
                onSendMessage={(text) => handleSendMessage(text, [])}
                onSetReplyText={handleSetReplyText}
                onSwitchTab={onSwitchTab}
              />
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input Bar */}
      <div className="flex-shrink-0 mx-auto w-full max-w-3xl px-2 sm:px-0">
        <ChatInputBar 
          onSendMessage={handleSendMessage} 
          disabled={isStreaming}
          replyToText={replyToText}
          onClearReply={() => setReplyToText(null)}
          showAttachments={false}
          onSearchStateChange={setIsSearchEnabled}
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
          userPlan={userPlan}
          onUpgradeRequest={handleUpgradeRequest}
        />
      </div>
    </div>
  );
};
